import {
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  KeyboardArrowDownRounded,
  ManageSearchRounded,
} from "@mui/icons-material";
import { Badge, Button, Divider, Popover, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { TreeView } from "../../../../components";
import { ITreeItem } from "../../../../models";
import { getFullLabel, getShortLabel } from "../utilities";

const DEFAULT_BUTTON_TEXT = "Select Team";

interface IProps {
  isLoading: boolean;
  teams: ITreeItem[];
  selectedTeamGuids: string[];
  isMeVisible?: boolean;
  clearable?: boolean;
  onTeamChanged: (teams: ITreeItem[]) => void;
  onTeamCleared: () => void;
}

const CoherenceTeamPicker = (props: IProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [buttonText, setButtonText] = useState(DEFAULT_BUTTON_TEXT);
  const [tooltipText, setTooltipText] = useState<React.ReactNode | null>(null);

  useEffect(() => {
    const selectedTeams = props.teams.filter((x) => props.selectedTeamGuids.includes(x.itemId));
    const shortLabel = getShortLabel(selectedTeams);
    const fullLabel = getFullLabel(selectedTeams);

    if (shortLabel) {
      setButtonText(shortLabel);
    } else if (props.isMeVisible) {
      setButtonText("Me");
    } else {
      setButtonText(DEFAULT_BUTTON_TEXT);
    }

    if (fullLabel) {
      setTooltipText(
        <>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Selected</Typography>
          <Typography sx={{ fontSize: 13 }}>{fullLabel}</Typography>
        </>,
      );
    } else {
      setTooltipText(null);
    }
  }, [props.isMeVisible, props.teams, props.selectedTeamGuids]);

  return (
    <>
      <Badge color="primary" variant="dot" invisible={props.selectedTeamGuids.length === 0}>
        <Tooltip
          title={tooltipText}
          placement="bottom-start"
          slotProps={{
            tooltip: {
              sx: {
                maxHeight: "400px",
                overflowY: "auto",
              },
            },
          }}
        >
          <Button
            variant="tertiary"
            startIcon={<ManageSearchRounded />}
            endIcon={<KeyboardArrowDownRounded />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            {buttonText}
          </Button>
        </Tooltip>
      </Badge>

      <Popover
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Stack spacing={0.5} sx={{ maxHeight: "400px", overflow: "auto", p: 0.5, pt: 1 }}>
          {props.isMeVisible && (
            <>
              <Stack
                direction="row"
                spacing={0.25}
                sx={{ alignItems: "center", "&&": { ml: 1.5 }, cursor: "pointer" }}
                onClick={() => props.onTeamChanged([])}
              >
                {props.selectedTeamGuids.length === 0 ? <CheckBoxRounded /> : <CheckBoxOutlineBlankRounded />}
                <Typography>Me</Typography>
              </Stack>
              <Divider variant="dashed" sx={{ color: "#c9c9c9", "&&": { mx: 0.5 } }} />
            </>
          )}

          {props.isLoading && (
            <Stack>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} width={240} />
              ))}
            </Stack>
          )}

          {!props.isLoading && (
            <TreeView
              items={props.teams}
              selectedItemIds={props.selectedTeamGuids}
              onItemSelected={props.onTeamChanged}
            />
          )}
        </Stack>

        {props.clearable && (
          <Stack spacing={0.5} sx={{ mx: 1, mb: 0.5 }}>
            <Divider variant="dashed" sx={{ color: "#c9c9c9", mt: "0 !important", mx: 1 }} />
            <Button variant="tertiary" sx={{ alignSelf: "start" }} onClick={props.onTeamCleared}>
              Clear
            </Button>
          </Stack>
        )}
      </Popover>
    </>
  );
};

export { CoherenceTeamPicker };
